import ComponentState, { ComponentStateTypes } from "@/components/common/ComponentState";
import NoData from "@/components/common/NoData";
import PropertyView from "@/components/common/views/PropertyView";
import { api } from "@/config/axios-context";
import { AccommodationLastNYearsStatsModel } from "@/types/acommodation/acc-stats-types";
import useAppContext from "@/utilities/hooks/useAppContext";
import { useEffect, useState } from "react";
import AppStackedBarChart from "@/components/common/charts/AppStackedBarChart";
import { data, useParams, useSearchParams } from "react-router-dom";
import { URI_PARAMETER_KEYS } from "@/config/router";
import { months } from "@/utilities/calendar-utilities";
import { AppColors } from "@/config/app-constants";

type AccommodationLastNYeasStatsProps = {
    lastNYearsData?: number,
    seasonId?: number
}

const propertyViewClassName = `
w-full lg:w-auto
bg-neutral-100 dark:bg-neutral-900
rounded-lg
p-4
`;

const AccommodationLastNYeasStats = (props: AccommodationLastNYeasStatsProps) => {
    const context = useAppContext();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const [searchQueryParams] = useSearchParams();
    var params = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const [data, setData] = useState<AccommodationLastNYearsStatsModel>({
        seasons: [],
        roomTypes: [],
        channels: []
    });

    useEffect(() => {
        loadData().then();
    }, [props.seasonId, params[URI_PARAMETER_KEYS.TARGET_SEASON_ID], searchQueryParams]);

    const loadData = async () => {
        const year = new Date().getFullYear();
        let fromDate: string | undefined = undefined;
        let toDate: string | undefined = undefined;
        const filterType = searchQueryParams.get('filterType');
        switch (filterType) {
            case 'month':
                const monthFilter = searchQueryParams.get('monthFilter');
                const monthIndex = months.findIndex(m => m === monthFilter);
                fromDate = `${year}-${(monthIndex + 1).toString().padStart(2, '0')}-01`;
                toDate = `${year}-${(monthIndex + 1).toString().padStart(2, '0')}-${new Date(year, monthIndex + 1, 0).getDate()}`;

                break;
            case 'between':
                const filterDates = searchQueryParams.get('filterDates')?.split('-');
                fromDate = filterDates?.[0];
                toDate = filterDates?.[1];
                break;
            case 'until':
                const untilDate = searchQueryParams.get('untilDate');
                fromDate = `${year - 1}-01-01`;
                toDate = untilDate || undefined;
                break;
        }

        try {
            const response = await api.get<AccommodationLastNYearsStatsModel>({
                url: 'AccommodationStates/StatesLastNYears',
                params: {
                    seasonId: props.seasonId,
                    fromDate: fromDate || '',
                    toDate: toDate || ''
                }
            });

            if (!response.success) {
                throw response.statusText;
            }

            const responseData = response.data;
            responseData.channels = [
                { id: 0, name: context.t('accommodation.stats.direct'), color: AppColors.SUNRISE },
                ...responseData.channels
            ]

            setData(responseData);
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('nodata');
            throw error;
        }
    }

    function dataToDataset() {
        const dataset = [
            {
                id: 'neto',
                label: context.t('agriculture.stats.neto'),
                data: data.seasons.map((item) => item.data.neto.toFixed(2)),
                color: '#7dc8faB3'
            },
            {
                id: 'expenses',
                label: context.t('accommodation.stats.expenses'),
                data: data.seasons.map((item) => (item.data.work.totalSum + item.data.booking.channelsData.totalSum).toFixed(2)),
                color: '#fa9bafB3'
            }
        ]

        return dataset;
    }

    function nightsDataset() {
        const roomTypeDataset = data.roomTypes.map(roomType => {
            return {
                id: roomType.id,
                label: roomType.name,
                color: roomType.color ? roomType.color + 'B3' : undefined,
                data: data.seasons.map((item) => {
                    const roomTypeStats = item.data.booking.roomTypeStats?.find(x => x.id === roomType.id);
                    if (!roomTypeStats) return 0;
                    return roomTypeStats.stats?.nights || item.data.booking.nights;
                })
            }
        });

        return roomTypeDataset;
    }

    function cancellationsDataset() {
        const dataSet = [
            {
                id: 'cancellations',
                color: '#fa9bafB3',
                label: context.t('accommodation.stats.cancellations'),
                data: data.seasons.map((item) => {
                    return item.data.booking.canceled.count
                }),
            }
        ]

        return dataSet;
    }

    function channelDataset() {
        const channelDataset = data.channels.map(channel => {
            return {
                id: channel.id,
                label: channel.name,
                color: channel.color ? channel.color + 'B3' : undefined,
                data: data.seasons.map((item) => {
                    const channelStats = item.data.booking.channelsData.items?.find(x => x.id === channel.id);
                    if (!channelStats) return 0;
                    return channelStats.count;
                })
            }
        });

        return channelDataset;
    }

    function roomTypeDataset() {
        const roomTypeDataset = data.roomTypes.map(roomType => {
            return {
                id: roomType.id,
                label: roomType.name,
                color: roomType.color ? roomType.color + 'B3' : undefined,
                data: data.seasons.map((item) => {
                    const roomTypeStats = item.data.booking.roomTypeStats?.find(x => x.id === roomType.id);
                    if (!roomTypeStats) return 0;
                    return roomTypeStats.stats?.count || item.data.booking.count;
                })
            }
        });

        return roomTypeDataset;
    }

    function workDataset() {
        const dataSet = [
            {
                id: 'work-maintenance',
                color: '#fa9bafB3',
                label: context.t('work.types.maintenance'),
                data: data.seasons.map((item) => {
                    return item.data.work.maintenanceTotalSum
                }),
            }, 
            {
                id: 'work-investment',
                color: '#7dc8faB3',
                label: context.t('work.types.investment'),
                data: data.seasons.map((item) => {
                    return item.data.work.investmentTotalSum
                }),
            }
        ]

        return dataSet;
    }

    return (
        <div>
            <ComponentState state={componentState} >
                {data?.seasons?.length > 0 ? (
                    <div className="p-4 w-full flex flex-wrap gap-6">
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.profit"
                        >
                            <AppStackedBarChart
                                name="somethings"
                                legend
                                data={{
                                    labels: data.seasons.map((item) => `'${item.season.name.slice(-2)}`),
                                    dataSet: dataToDataset(),
                                    totals: data.seasons.map((item) => item.data.bruto.toFixed(2))
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.roomTypeReservations"
                        >
                            <AppStackedBarChart
                                name="room-type-total-acc-stats"
                                legend
                                data={{
                                    labels: data.seasons.map((item) => `'${item.season.name.slice(-2)}`),
                                    dataSet: roomTypeDataset(),
                                    totals: data.seasons.map((item) => item.data.booking.count)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.channelReservations"
                        >
                            <AppStackedBarChart
                                name="channels-total-acc-stats"
                                legend
                                data={{
                                    labels: data.seasons.map((item) => `'${item.season.name.slice(-2)}`),
                                    dataSet: channelDataset(),
                                    totals: data.seasons.map((item) => item.data.booking.count)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.nightsTotalAverage"
                        >
                            <AppStackedBarChart
                                name="nights-total-acc-stats"
                                legend
                                data={{
                                    labels: data.seasons.map((item) => `'${item.season.name.slice(-2)}`),
                                    dataSet: nightsDataset(),
                                    totals: data.seasons.map((item) => item.data.booking.nights)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.cancellations"
                        >
                            <AppStackedBarChart
                                name="channels-total-acc-stats"
                                legend
                                data={{
                                    labels: data.seasons.map((item) => `'${item.season.name.slice(-2)}`),
                                    dataSet: cancellationsDataset(),
                                    totals: data.seasons.map((item) => item.data.booking.canceled.count)
                                }} />
                        </PropertyView>
                        <PropertyView
                            className={propertyViewClassName}
                            label="accommodation.stats.workExpenses"
                        >
                            <AppStackedBarChart
                                name="work-total-acc-stats"
                                legend
                                data={{
                                    labels: data.seasons.map((item) => `'${item.season.name.slice(-2)}`),
                                    dataSet: workDataset(),
                                    totals: data.seasons.map((item) => item.data.work.totalSum)
                                }} />
                        </PropertyView>
                    </div>
                ) : (
                    <NoData />
                )}
            </ComponentState>
        </div >
    );
};

export default AccommodationLastNYeasStats;